import { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { ShieldCheck, LayoutDashboard, Users, CalendarClock, FileBadge, Flame, CreditCard, Mail, UserCog, ArrowLeftRight, LogOut, KeyRound, Sun, Moon, Menu, X, Newspaper, FireExtinguisher, ScrollText, Palette, Megaphone } from "lucide-react";
import { useAuth, api, formatApiError, canDo } from "../context/AuthContext";
import { useBranding } from "../context/BrandingContext";
import { RoleBadge } from "./RoleBadge";
import AnnoncesPopup from "./AnnoncesPopup";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

const NAV = [
  { to: "/", label: "Tableau de bord", icon: LayoutDashboard, testid: "nav-dashboard-link", end: true, menuKey: "dashboard" },
  { to: "/salaries", label: "Salariés", icon: Users, testid: "nav-employees-link", cat: "sst", menuKey: "salaries" },
  { to: "/certificats", label: "Certificats SST", icon: FileBadge, testid: "nav-certificats-link", iconClass: "text-emerald-500", cat: "sst", menuKey: "certificats" },
  { to: "/ssiap", label: "SSIAP", icon: Flame, testid: "nav-ssiap-link", iconClass: "text-red-500", cat: "ssiap", menuKey: "ssiap" },
  { to: "/cartes-pro", label: "Cartes Pro", icon: CreditCard, testid: "nav-cartes-pro-link", iconClass: "text-orange-500", cat: "carte_pro", menuKey: "carte_pro" },
  { to: "/formations-incendie", label: "Formations Incendie", icon: FireExtinguisher, testid: "nav-incendie-link", iconClass: "text-sky-500", cat: "incendie", menuKey: "incendie" },
  { to: "/sessions", label: "Sessions MAC", icon: CalendarClock, testid: "nav-sessions-link", cat: "sessions", menuKey: "sessions" },
  { to: "/rappels", label: "Rappels", icon: Mail, testid: "nav-rappels-link", cat: "rappels", menuKey: "rappels" },
  { to: "/actualites", label: "Actualités", icon: Newspaper, testid: "nav-actualites-link", cat: "actualites", menuKey: "actualites" },
  { to: "/mises-a-jour", label: "Mises à jour", icon: Megaphone, testid: "nav-mises-a-jour-link", menuKey: "mises_a_jour" },
  { to: "/journal", label: "Journal", icon: ScrollText, testid: "nav-journal-link", adminOnly: true, menuKey: "journal" },
  { to: "/utilisateurs", label: "Utilisateurs", icon: UserCog, testid: "nav-users-link", adminOnly: true, menuKey: "utilisateurs" },
  { to: "/personnalisation", label: "Personnalisation", icon: Palette, testid: "nav-personnalisation-link", adminOnly: true, menuKey: "personnalisation" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { label: navLabel, imageUrl, branding } = useBranding();
  const site = branding?.site || {};
  const titreSite = [site.titre_l1, site.titre_l2].filter(Boolean).join(" ") || "Suivi des Certifications / Formations";
  const logoSrc = imageUrl("logo") || "/logo.png";
  const [roles, setRoles] = useState([]);
  useEffect(() => {
    api.get("/roles").then((r) => setRoles(r.data.roles || [])).catch(() => {});
  }, []);
  const myBadge = roles.find((r) => r.nom === user?.role)?.badge;
  const navigate = useNavigate();
  const [theme, setTheme] = useState(() => localStorage.getItem("theme") || "dark");

  const toggleTheme = () => {
    const next = theme === "dark" ? "light" : "dark";
    localStorage.setItem("theme", next);
    document.documentElement.classList.toggle("light", next === "light");
    setTheme(next);
  };
  const [pwOpen, setPwOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [saving, setSaving] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const changePassword = async () => {
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Mot de passe modifié");
      setPwOpen(false);
      setCurrent(""); setNext("");
    } catch (e) {
      toast.error(formatApiError(e, "Échec de la modification"));
    } finally {
      setSaving(false);
    }
  };

  const navItems = (onClick) => NAV.filter((n) => (!n.adminOnly || user?.role === "admin") && (!n.cat || canDo(user, n.cat, "voir"))).map(({ to, label: defaultLabel, menuKey, icon: Icon, testid, end, iconClass }) => (
    <NavLink key={to} to={to} end={end} data-testid={testid} onClick={onClick}
      className={({ isActive }) =>
        `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
          isActive ? "bg-primary/15 text-primary" : "text-muted-foreground hover:bg-secondary hover:text-foreground"}`}>
      <Icon className={`h-4 w-4 ${iconClass || ""}`} /> {navLabel(menuKey, defaultLabel)}
    </NavLink>
  ));

  return (
    <div className="min-h-screen bg-background" data-testid="app-layout">
      <aside className="hidden md:flex fixed inset-y-0 left-0 w-60 flex-col border-r border-border bg-card/50 p-4 z-40">
        <div className="flex items-center gap-2.5 px-2 py-3 mb-4">
          <img src={logoSrc} alt={titreSite} className="h-14 w-14 rounded-xl object-cover shadow" data-testid="app-logo" />
          <div>
            <p className="font-bold text-xs leading-tight" style={{ fontFamily: "Outfit" }}>{site.titre_l1 || "Suivi des Certifications"}<br />{site.titre_l2 || "/ Formations"}</p>
            <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mt-0.5">{site.signature || "by Gerlier Sean"}</p>
          </div>
        </div>
        <nav className="flex-1 space-y-1">{navItems()}</nav>
        <div className="border-t border-border pt-3 space-y-1.5">
          <div className="flex items-center gap-2.5 rounded-lg bg-secondary/60 px-3 py-2" data-testid="current-user-card">
            <div className="h-8 w-8 rounded-full bg-primary/20 text-primary flex items-center justify-center text-xs font-bold shrink-0">
              {(user?.name || user?.email || "?").slice(0, 1).toUpperCase()}
            </div>
            <div className="min-w-0">
              <p className="text-xs font-semibold truncate" data-testid="user-name">{user?.name || user?.email}</p>
              <p className="text-[10px] text-muted-foreground truncate" data-testid="user-email">{user?.email}</p>
            </div>
            <span className="ml-auto shrink-0" data-testid="user-role">
              <RoleBadge role={user?.role || "?"} badge={myBadge} />
            </span>
          </div>
          <Button variant="ghost" size="sm" className="w-full justify-start text-muted-foreground" data-testid="switch-user-button" onClick={handleLogout}>
            <ArrowLeftRight className="h-4 w-4 mr-2" /> Changer d'utilisateur
          </Button>
          <Button variant="ghost" size="sm" className="w-full justify-start text-muted-foreground" data-testid="theme-toggle-button" onClick={toggleTheme}>
            {theme === "dark" ? <Sun className="h-4 w-4 mr-2" /> : <Moon className="h-4 w-4 mr-2" />}
            {theme === "dark" ? "Thème clair" : "Thème sombre"}
          </Button>
          <Button variant="ghost" size="sm" className="w-full justify-start text-muted-foreground" data-testid="change-password-button" onClick={() => setPwOpen(true)}>
            <KeyRound className="h-4 w-4 mr-2" /> Mot de passe
          </Button>
          <Button variant="ghost" size="sm" className="w-full justify-start text-muted-foreground hover:text-red-400" data-testid="logout-button" onClick={handleLogout}>
            <LogOut className="h-4 w-4 mr-2" /> Déconnexion
          </Button>
        </div>
      </aside>

      <div className="md:hidden sticky top-0 z-40 border-b border-border bg-card/80 backdrop-blur px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src={logoSrc} alt={titreSite} className="h-9 w-9 rounded-lg object-cover" data-testid="app-logo-mobile" />
            <span className="font-bold text-sm" style={{ fontFamily: "Outfit" }}>{site.titre_l1 || "Suivi des Certifications"}</span>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle-button-mobile">
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
            <Button variant="ghost" size="icon" data-testid="change-password-button" onClick={() => setPwOpen(true)}><KeyRound className="h-4 w-4" /></Button>
            <Button variant="ghost" size="icon" data-testid="logout-button" onClick={handleLogout}><LogOut className="h-4 w-4" /></Button>
            <Button variant="ghost" size="icon" data-testid="mobile-menu-button" onClick={() => setMenuOpen(!menuOpen)}>
              {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>
        {menuOpen && (
          <nav className="pt-3 space-y-1 [&>a]:py-3.5 [&>a]:text-base">
            {navItems(() => setMenuOpen(false))}
            <div className="flex items-center gap-2 px-3 py-2 mt-2 border-t border-border" data-testid="mobile-current-user">
              <span className="text-xs text-muted-foreground truncate">{user?.email}</span>
              <span className="ml-auto shrink-0"><RoleBadge role={user?.role || "?"} badge={myBadge} /></span>
            </div>
          </nav>
        )}
      </div>

      <main className="md:ml-60 p-4 sm:p-6 lg:p-8 overflow-x-hidden min-w-0">
        <Outlet />
      </main>

      <AnnoncesPopup />

      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent data-testid="password-dialog">
          <DialogHeader><DialogTitle>Modifier le mot de passe</DialogTitle><DialogDescription>Saisissez votre mot de passe actuel puis le nouveau.</DialogDescription></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="pw-current">Mot de passe actuel</Label>
              <Input id="pw-current" type="password" data-testid="password-current-input" value={current} onChange={(e) => setCurrent(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pw-next">Nouveau mot de passe (8 caractères min.)</Label>
              <Input id="pw-next" type="password" data-testid="password-new-input" value={next} onChange={(e) => setNext(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={changePassword} disabled={saving || !current || next.length < 8} data-testid="password-submit-button">
              {saving ? "Enregistrement..." : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
